import { useState, useEffect } from "react";
import { updateUsersForm } from "../../lib/actions";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiUploadCloud, FiEye, FiEyeOff } from "react-icons/fi";
import { MdOutlineDeleteOutline } from "react-icons/md";

export default function EditUsersUi({ userData }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  // Initialize states based on userData
  const [userType, setUserType] = useState(userData?.type || "user");
  const [verified, setVerified] = useState(userData?.confirmed || false);
  
  // Initialize profile photo with existing URL if present
  const [userPhoto, setUserPhoto] = useState(
    userData?.photo ? { url: userData.photo, file: null } : null
  );

  // States for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { i18n } = useTranslation();
  const t = i18n.t.bind(i18n);
  const currentLang = i18n.language;

  useEffect(() => {
    if (userData) {
      setUserType(userData.type || "user");
      setVerified(userData.confirmed || false);
      if (userData.photo && !userPhoto?.file) {
        setUserPhoto({ url: userData.photo, file: null });
      }
    }
  }, [userData]);

  useEffect(() => {
    return () => {
      // Only revoke object URLs created from local files, not the original web URL
      if (userPhoto?.file && userPhoto?.url) URL.revokeObjectURL(userPhoto.url);
    };
  }, [userPhoto]);

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (userPhoto?.file && userPhoto?.url) URL.revokeObjectURL(userPhoto.url);
      setUserPhoto({
        file: file,
        url: URL.createObjectURL(file),
      });
    }
  };

  const handleDeletePhoto = () => {
    if (userPhoto?.file && userPhoto?.url) URL.revokeObjectURL(userPhoto.url);
    setUserPhoto(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    formData.append("verified", verified ? "1" : "0");
    
    if (userData?.id) {
      formData.append("id", userData.id);
    }

    if (userPhoto?.file) {
      formData.append("photo", userPhoto.file);
    }

    await updateUsersForm(userData.id, formData, navigate);
    setLoading(false);
  };

  const navigate = (route) => {
    nav(route);
  };

  const handleChangeCheck = () => {
    setVerified(!verified);
  };

  if (!userData) {
    return <div className="text-white text-center mt-10">{currentLang === "en" ? "Loading..." : "جاري التحميل..."}</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      
      {/* Profile Photo Section */}
      <div className="mb-6 flex flex-col items-center sm:items-center">
        <label className="block text-white mb-2">
          {currentLang === "en" ? "Profile Photo" : "الصورة الشخصية"}
        </label>
        <div className="bg-thirdly p-4 rounded-2xl border-2 border-dashed border-gray-500/50 hover:border-secondary transition-colors h-40 w-60 flex flex-col justify-center items-center relative overflow-hidden group">
          {!userPhoto ? (
            <div className="text-center w-full h-full flex flex-col items-center justify-center cursor-pointer">
              <FiUploadCloud className="w-10 h-10 text-gray-400 mb-1 group-hover:text-secondary transition-colors" />
              <span className="text-xs text-gray-300 text-center px-2 group-hover:text-white transition-colors">
                {currentLang === "en" ? "Upload Photo" : "رفع صورة"}
              </span>
              <input
                type="file"
                accept="image/*"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handlePhotoChange}
              />
            </div>
          ) : (
            <div className="relative w-full h-full flex justify-center items-center">
              <img
                src={userPhoto.url}
                alt="Profile Preview"
                className="h-full w-full object-cover rounded-lg border border-gray-600"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center items-center">
                <button
                  type="button"
                  className="bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                  onClick={handleDeletePhoto}
                >
                  <MdOutlineDeleteOutline className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* General User Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-1 gap-4 pb-3">
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "First Name" : "الاسم الأول"} </label>
          <input
            type="text"
            name="first_name"
            defaultValue={userData?.first_name || ""}
            placeholder={currentLang === "en" ? "Enter first name" : "أدخل الاسم الأول"}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            
          />
        </div>
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "Last Name" : "الاسم الأخير"} </label>
          <input
            type="text"
            name="last_name"
            defaultValue={userData?.last_name || ""}
            placeholder={currentLang === "en" ? "Enter last name" : "أدخل الاسم الأخير"}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            
          />
        </div>
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "Username" : "اسم المستخدم"} </label>
          <input
            type="text"
            name="user_name"
            defaultValue={userData?.user_name || ""}
            placeholder={currentLang === "en" ? "Enter username" : "أدخل اسم المستخدم"}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            
          />
        </div>
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "Email" : "البريد الإلكتروني"} </label>
          <input
            type="email"
            name="email"
            defaultValue={userData?.email || ""}
            placeholder={currentLang === "en" ? "Enter email" : "أدخل البريد الإلكتروني"}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            
          />
        </div>
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "Phone Number" : "رقم الهاتف"} </label>
          <input
            type="text"
            name="phone_number"
            defaultValue={userData?.phone_number || ""}
            placeholder={currentLang === "en" ? "Enter phone number" : "أدخل رقم الهاتف"}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            
          />
        </div>
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "Bio" : "النبذة (Bio)"} </label>
          <input
            type="text"
            name="bio"
            defaultValue={userData?.bio || ""}
            placeholder={currentLang === "en" ? "Enter bio" : "أدخل النبذة"}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
          />
        </div>
        
        {/* Adjusted Password Field */}
        <div className="mb-4">
          <label className="block text-white mb-2"> 
            {currentLang === "en" ? "Password (Leave blank to keep current)" : "كلمة المرور (اتركه فارغاً للاحتفاظ بالكلمة الحالية)"} 
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={currentLang === "en" ? "Enter new password" : "أدخل كلمة المرور الجديدة"}
              className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors ${
                currentLang === "ar" ? "left-3" : "right-3"
              }`}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
        </div>

        {/* Adjusted Password Confirmation Field */}
        <div className="mb-4">
          <label className="block text-white mb-2"> {currentLang === "en" ? "Password Confirmation" : "تأكيد كلمة المرور"} </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="password_confirmation"
              placeholder={currentLang === "en" ? "Confirm new password" : "تأكيد كلمة المرور الجديدة"}
              className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors ${
                currentLang === "ar" ? "left-3" : "right-3"
              }`}
            >
              {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
        </div>

      </div>

      {/* Verified Toggle */}
      <div className="flex gap-4 sm:flex-row flex-col sm:justify-between items-start mt-4 mb-4">
        <div className="flex flex-row justify-center items-center gap-6">
          <div className="flex items-center gap-3">
            <div
              onClick={handleChangeCheck}
              className={`relative w-14 h-6 rounded-full cursor-pointer transition ${
                verified ? "bg-secondary" : "bg-gray-400"
              }`}
            >
              <span
                className={`w-5 h-4 mt-1 bg-white rounded-full shadow-md block transform transition ${
                  verified
                    ? currentLang === "ar"
                      ? "-translate-x-1"
                      : "translate-x-7"
                    : currentLang === "ar"
                      ? "-translate-x-7"
                      : "translate-x-1"
                }`}
              />
            </div>
            <label className="text-white">
              {verified
                ? currentLang === "en"
                  ? "Verified"
                  : "موثق"
                : currentLang === "en"
                  ? "Un Verified"
                  : "غير موثق"}
            </label>
          </div>
        </div>
      </div>

      {/* User Type Selection */}
      {verified && (
        <div className="mb-4">
          <label className="block text-white mb-2">
            {currentLang === "en" ? "User Type" : "نوع الحساب"}
          </label>
          <select
            name="type"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
            
          >
            <option value="user">{currentLang === "en" ? "User" : "مستخدم"}</option>
            <option value="company">{currentLang === "en" ? "Company" : "شركة"}</option>
          </select>
        </div>
      )}

      {/* Verification Documents */}
      {verified && (
        <>
          <hr className="border-gray-500/50 my-4" />
          <h3 className="text-white text-lg mb-4">
            {currentLang === "en" ? "Verification Documents" : "مستندات التوثيق"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-1 gap-4 pb-3">
            <div className="mb-4">
              <label className="flex items-center justify-between text-white mb-2">
                <span>{currentLang === "en" ? "Confirmation Photo 1" : "صورة التوثيق 1"}</span>
                {userData?.confirm_photo_1 && (
                  <a href={userData.confirm_photo_1} target="_blank" rel="noreferrer" className="text-secondary text-sm hover:underline">
                    {currentLang === "en" ? "View Current" : "عرض الحالي"}
                  </a>
                )}
              </label>
              <input
                type="file"
                name="confirm_photo_1"
                accept="image/*"
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-gray-400 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-white hover:file:bg-primary cursor-pointer"
              />
            </div>

            {userType === "user" && (
              <div className="mb-4">
                <label className="flex items-center justify-between text-white mb-2">
                  <span>{currentLang === "en" ? "Confirmation Photo 2" : "صورة التوثيق 2"}</span>
                  {userData?.confirm_photo_2 && (
                    <a href={userData.confirm_photo_2} target="_blank" rel="noreferrer" className="text-secondary text-sm hover:underline">
                      {currentLang === "en" ? "View Current" : "عرض الحالي"}
                    </a>
                  )}
                </label>
                <input
                  type="file"
                  name="confirm_photo_2"
                  accept="image/*"
                  className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-gray-400 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-white hover:file:bg-primary cursor-pointer"
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Company Fields */}
      {verified && userType === "company" && (
        <>
          <hr className="border-gray-500/50 my-4" />
          <h3 className="text-white text-lg mb-4">
            {currentLang === "en" ? "Company Information" : "معلومات الشركة"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-1 gap-4 pb-3">
            <div className="mb-4">
              <label className="block text-white mb-2"> {currentLang === "en" ? "Company Name" : "اسم الشركة"} </label>
              <input
                type="text"
                name="company_name"
                defaultValue={userData?.company_name || ""}
                placeholder={currentLang === "en" ? "Enter company name" : "أدخل اسم الشركة"}
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                
              />
            </div>
            <div className="mb-4">
              <label className="block text-white mb-2"> {currentLang === "en" ? "Company Email" : "البريد الإلكتروني للشركة"} </label>
              <input
                type="email"
                name="company_email"
                defaultValue={userData?.company_email || ""}
                placeholder={currentLang === "en" ? "Enter company email" : "أدخل البريد الإلكتروني للشركة"}
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                
              />
            </div>
            <div className="mb-4">
              <label className="block text-white mb-2"> {currentLang === "en" ? "Company Phone" : "رقم هاتف الشركة"} </label>
              <input
                type="text"
                name="company_phone"
                defaultValue={userData?.company_phone || ""}
                placeholder={currentLang === "en" ? "Enter company phone" : "أدخل رقم هاتف الشركة"}
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                
              />
            </div>
            <div className="mb-4">
              <label className="block text-white mb-2"> {currentLang === "en" ? "Commercial Register Number" : "رقم السجل التجاري"} </label>
              <input
                type="text"
                name="commercial_register_number"
                defaultValue={userData?.commercial_register_number || ""}
                placeholder={currentLang === "en" ? "Enter register number" : "أدخل رقم السجل التجاري"}
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                
              />
            </div>
            <div className="mb-4">
              <label className="block text-white mb-2"> {currentLang === "en" ? "Address" : "العنوان"} </label>
              <input
                type="text"
                name="address"
                defaultValue={userData?.address || ""}
                placeholder={currentLang === "en" ? "Enter address" : "أدخل العنوان"}
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
                
              />
            </div>
            <div className="mb-4">
              <label className="block text-white mb-2"> {currentLang === "en" ? "Company Website" : "موقع الشركة"} </label>
              <input
                type="url"
                name="company_website"
                defaultValue={userData?.company_website || ""}
                placeholder={currentLang === "en" ? "Enter company website" : "أدخل موقع الشركة"}
                className="w-full px-4 py-2 bg-thirdly border border-gray-500/100 text-white rounded-lg"
              />
            </div>
          </div>
        </>
      )}

      {/* Submit Button */}
      <div className="flex justify-center w-full mt-5">
        <button
          type="submit"
          disabled={loading}
          className={`w-full sm:w-2/5 md:w-[14%] bg-secondary text-white py-2 rounded-lg transition flex items-center justify-center ${loading ? "cursor-not-allowed" : "hover:bg-primary"}`}
        >
          {loading ? (
            <svg
              className="animate-spin h-5 w-5 "
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
          ) : (
            t("button.save")
          )}
        </button>
      </div>
    </form>
  );
}